import React, { type RefObject } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Box } from '@atlaskit/primitives';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';
import { token } from '@atlaskit/tokens';

import {
	ReactionContainerType,
	ReactionLocation,
} from '@confluence/reactions/entry-points/reactionTypes.ts';
import { LIVE_PAGE_SUBTYPE } from '@confluence/live-pages-utils';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { ReactionsLoader } from '@confluence/reactions/entry-points/ReactionsLoader.tsx';
import { fg } from '@confluence/feature-gating';
import { useIsEligibleReactionsFeedbackPageReturn } from '@confluence/experiment-reactions-feedback-page-return/entry-points/useIsEligibleReactionsFeedbackPageReturn.ts';

const i18n = defineMessages({
	addNew: {
		id: 'live-pages-features.byline.reactions.add.new',
		description: 'Message displayed in button to add a new reaction.',
		defaultMessage: 'Add new',
	},
	addReaction: {
		id: 'live-pages-features.byline.reactions.add.reaction',
		description: 'Message displayed to add a reaction.',
		defaultMessage: 'Add reaction',
	},
});

const dotStyles = xcss({
	height: token('space.050'),
	width: token('space.050'),
	backgroundColor: 'color.background.brand.bold',
	borderRadius: 'radius.full',
	alignSelf: 'center',
	transition: 'opacity 300ms ease-in-out',
});

export type LivePagesByLineReactionsProps = {
	contentId: string;
	spaceId: string;
	isDisabled?: boolean;
	contentType?: string;
	isCurrentUserContributor?: boolean;
	editorBylineContainerRef?: RefObject<HTMLDivElement>;
};

const floatingContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'left',
	transition: 'left 600ms',
	marginBottom: 'space.025',
	height: '28px',
	marginTop: 'space.025',
});

export const LivePagesByLineReactions = ({
	contentId,
	spaceId,
	isDisabled = false,
	contentType,
	isCurrentUserContributor = false,
	editorBylineContainerRef = undefined,
}: LivePagesByLineReactionsProps) => {
	const { formatMessage } = useIntl();
	const isLivePage = useIsCurrentPageLive();

	// Used when we want to allow the user to select reactions from the summary view popup
	const customIcon = (
		<EmojiAddIcon testId="emoji-add-icon" color={token('color.icon.subtle')} label="" />
	);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

	const { isEligible: isEligibleReactionsFeedbackPageReturn } =
		useIsEligibleReactionsFeedbackPageReturn({
			contentId,
			isContributor: Boolean(isCurrentUserContributor),
			skip: !fg('cc-engagement-reaction-feedback-page-return-kill'),
			source: 'livePage',
		});

	return (
		<Box xcss={floatingContainerStyles} testId="live-pages-reactions">
			<ReactionsLoader
				contentId={contentId}
				location={contentType === 'blogpost' ? ReactionLocation.BLOGPOST : ReactionLocation.PAGE}
				containerId={spaceId}
				containerType={ReactionContainerType.SPACE}
				contentSubType={isLivePage ? LIVE_PAGE_SUBTYPE : null}
				summaryViewEnabled
				summaryViewThreshold={1}
				allowSelectFromSummaryView
				useButtonAlignmentStyling
				summaryViewPlacement="bottom-start"
				hideNoReactionsText
				readOnly={isDisabled}
				isViewOnly={isDisabled}
				isSubtle
				reactionPickerTriggerIcon={customIcon}
				reactionPickerTriggerText={formatMessage(i18n.addNew)}
				useDefaultStyledContainer
				hidePlaceholder={isSSR}
				noRelativeContainer
				noFlexWrap
				hoverableSummaryView
				optimisticallyLoadSummaryEmojis={isSSR}
				renderParticleEffectOnSummaryView
				bylineContainerRef={editorBylineContainerRef}
				renderParticleEffectOnLoad={isEligibleReactionsFeedbackPageReturn}
			/>
			{isEligibleReactionsFeedbackPageReturn && (
				<Box xcss={dotStyles} testId="reactions-feedback-experiment-live-dot" />
			)}
		</Box>
	);
};
